import React from 'react';
import PropTypes from 'prop-types';
import { convertToPercentString } from 'utilities';

const UsageBarLegend = ({ drm, id, ...props }) => {
  const error = drm.newPercent > drm.remainingAllowance;

  const classes = {
    list: {
      listStyleType: 'none',
      marginTop: '0.5em',
    },
    listItem: {
      alignItems: 'center',
      display: 'flex',
      gap: '0.5em',
      lineHeight: 1.5,
    },

    currentColor: {
      background: '#002242', // shelfBlue[800]
      outline: '1px solid #4d4d4d', // grey[700]
    },
    remainingColor: {
      background: error ? 'transparent' : '#DBEEFF', // shelfBlue[50]
      outline: error ? 'transparent' : '1px solid #4d4d4d', // grey[700]
    },
    selectionColor: {
      background: error ? '#D32F2F' : '#0071DB', //shelfBlue[500]
      outline: '1px solid #4d4d4d', // grey[700]
    },
    shape: {
      borderRadius: '50%',
      height: '.75em',
      outlineOffset: '-1px',
      width: '.75em',
    },

    legendText: {
      display: 'flex',
      flex: 1,
      fontFamily: 'Roboto',
      fontSize: '0.875rem',
      justifyContent: 'space-between',
    },
    percentDisplay: {
      fontWeight: 500,
    },
    percentError: {
      color: error ? '#D32F2F' : '',
    },
  };

  return (
    <ul id={`usage-bar-legend-${id}`} style={classes.list} {...props}>
      <li id={`current-percent-${id}`} style={classes.listItem}>
        <div
          aria-hidden="true"
          style={{ ...classes.currentColor, ...classes.shape }}
        ></div>
        <span style={classes.legendText}>
          {`${drm.usageText}: `}
          <b style={classes.percentDisplay} className="noTranslate">
            {convertToPercentString(drm.currentPercent)}
          </b>
        </span>
      </li>
      {drm.newPercent || drm.shouldShowNewPercent ? (
        <li id={`selection-percent-${id}`} style={classes.listItem}>
          <div
            aria-hidden="true"
            style={{ ...classes.selectionColor, ...classes.shape }}
          ></div>
          <span style={classes.legendText}>
            {`Current Selection: `}
            <b
              style={{ ...classes.percentDisplay, ...classes.percentError }}
              className="noTranslate"
            >
              {convertToPercentString(drm.newPercent)}
            </b>
          </span>
        </li>
      ) : null}
      <li id={`remaining-percent-${id}`} style={classes.listItem}>
        <div
          aria-hidden="true"
          style={{ ...classes.remainingColor, ...classes.shape }}
        ></div>
        <span style={classes.legendText}>
          {`Remaining Allowance: `}
          <b
            style={{ ...classes.percentDisplay, ...classes.percentError }}
            className="noTranslate"
          >
            {convertToPercentString(drm.remainingAllowance - drm.newPercent)}
          </b>
        </span>
      </li>
    </ul>
  );
};

UsageBarLegend.propTypes = {
  drm: PropTypes.shape({
    /**
     * Percent of DRM available
     */
    availablePercent: PropTypes.number.isRequired,
    /**
     * Percent of DRM that has been used
     */
    currentPercent: PropTypes.number.isRequired,
    /**
     * Percent of DRM user is attempting to add
     */
    newPercent: PropTypes.number,
    /**
     * Percent of DRM that is remaining
     */
    remainingAllowance: PropTypes.number.isRequired,
    /**
     * Should the legend always display the new percent list item
     */
    shouldShowNewPercent: PropTypes.bool,
    /**
     * Menu-specific label for currentPercent
     */
    usageText: PropTypes.string.isRequired,
  }).isRequired,
  /**
   * Helps connect bar graph rects to corresponding list items for screen reader
   */
  id: PropTypes.string.isRequired,
};

export default UsageBarLegend;
